import React, { memo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { kaReducer, Table } from 'ka-table';
import { DataType, SortingMode } from 'ka-table/enums';
import ProgressCell from './ProgressCell.jsx';

// Custom Search Component (memoized to prevent unnecessary re-renders)
const CustomSearchComponent = memo(({ value, onChange }) => (
  <div className="inputColor d-flex align-items-center rounded-5 px-2" style={{ width: '30%', margin: "5px 0 7px" }}>
    <input
      type="text"
      className="d-flex justify-content-center border-0 bg-transparent text-muted flex-grow-1 px-4 no-border"
      placeholder="Hinted search text"
      aria-label="Search"
      value={value}
      onChange={e => onChange(e.target.value)}
    />
    <button className="border-0 bg-transparent text-dark ms-2 me-2">
      <i className="fa-solid fa-magnifying-glass"></i>
    </button>
  </div>
));

export default function VendorsTable({
  vendors = [],
  pirm_addon__Status__c = 'Pending',
}) {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [tableProps, setTableProps] = useState({
    columns: [
      { key: 'rowIndex', title: '#', dataType: DataType.Number },
      { key: 'name', title: 'Name', dataType: DataType.String },
      { key: 'accountName', title: 'Account Name', dataType: DataType.String },
      { key: 'dateAssigned', title: 'Date Assigned', dataType: DataType.String },
      { key: 'dueDate', title: 'Due Date', dataType: DataType.String },
      { key: 'progress', title: 'Total Answered Questions', dataType: DataType.String },
      { key: 'chevron', title: '', dataType: DataType.String },
    ],
    data: [],
    rowKeyField: 'id',
    sortingMode: SortingMode.Single,
    paging: {
      enabled: true,
      pageIndex: 0,
      pageSize: 10,
      pageSizes: [10, 20, 30],
    },
  });

  // Load and format vendors
  useEffect(() => {
    if (!vendors || !Array.isArray(vendors)) return;

    const filteredVendors = vendors.filter(v =>
      pirm_addon__Status__c === 'Completed'
        ? v.pirm_addon__Status__c === 'Completed'
        : v.pirm_addon__Status__c !== 'Completed'
    );

    const formatted = filteredVendors.map((item, idx) => {
      const completedResponses = item?.pirm_addon__Audits__r?.records[0]?.pirm_addon__CompletedResponses__c;
      const totalResponses = item?.pirm_addon__Audits__r?.records[0]?.pirm_addon__TotalResponses__c;
      const completionPercentage = item?.pirm_addon__Audits__r?.records[0]?.pirm_addon__Response_Completion__c;

      return {
        id: item?.Id,
        rowIndex: idx + 1,
        name: item?.Name,
        accountName: item?.pirm_addon__Vendor__r?.Name || 'N/A',
        dateAssigned: formatDate(item?.CreatedDate),
        dueDate: formatDate(item?.pirm_addon__Due_Date__c),
        progress: `${completedResponses}/${totalResponses} (${completionPercentage}%)`,
        completedResponses,
        totalResponses,
        completionPercentage,
        rawData: item,
      };
    });

    setFilteredData(formatted);
    setTableProps(prev => ({ ...prev, data: formatted }));
  }, [vendors, pirm_addon__Status__c]);

  // Handle search input change
  const handleSearch = text => setSearchText(text);

  // Filter data when searchText or filteredData changes
  useEffect(() => {
    if (!filteredData.length) return;

    if (!searchText.trim()) {
      setTableProps(prev => ({ ...prev, data: filteredData }));
    } else {
      const term = searchText.toLowerCase().trim();
      const results = filteredData.filter(item =>
        tableProps.columns.some(col => {
          const value = item[col.key];
          return value != null && String(value).toLowerCase().includes(term);
        })
      );
      setTableProps(prev => ({ ...prev, data: results }));
    }
  }, [searchText, filteredData, tableProps.columns]);

  // Ka-Table dispatch
  const dispatch = action => setTableProps(prev => kaReducer(prev, action));

  // Date formatting helper
  const formatDate = dateStr => dateStr ? dateStr.split('T')[0].split('-').slice(1).join('/') : '';

  // Name cell — display only. Row-level onClick handles navigation.
  const NameCell = ({ value }) => (
    <span className="u-row-name vendorQuestionnaire">{value}</span>
  );

  const openQuestionnaire = (rowData) => {
    navigate('/questions', {
      state: {
        questionnaireId: rowData.rawData.Id,
        pirm_addon__Status__c: rowData.rawData.pirm_addon__Status__c,
        Audits_r: rowData.rawData.pirm_addon__Audits__r?.records[0]?.Id,
      },
    });
  };

  // Empty data placeholder
  const EmptyDataComponent = () => (
    <tr>
      <td colSpan={6} className="text-center py-3">
        No matching records found
      </td>
    </tr>
  );

  // Empty state
  if (!filteredData.length) {
    return (
      <p className="ms-2">
        {pirm_addon__Status__c === 'Completed' ? 'No Completed Assignments' : 'No Open Assignments'}
      </p>
    );
  }

  // Main render
  return (
    <div className="ms-1 p-0">
      <CustomSearchComponent
        value={searchText}
        onChange={handleSearch}
      />
      {tableProps.data.length > 0 ? (
        <Table
          {...tableProps}
          dispatch={dispatch}
          childComponents={{
            cellText: {
              content: props => {
                if (props.column.key === 'name') {
                  return <NameCell {...props} />;
                } else if (props.column.key === 'progress') {
                  return <ProgressCell {...props} />;
                } else if (props.column.key === 'chevron') {
                  return <i className="fa-solid fa-chevron-right u-row-chevron" aria-hidden="true" />;
                }
                return props.value;
              }
            },
            table: { elementAttributes: () => ({ className: 'rounded-table' }) },
            tableHead: { elementAttributes: () => ({ className: 'table-header' }) },
            headCell: { elementAttributes: () => ({ className: 'py-1 px-2' }) },
            dataRow: {
              elementAttributes: (props) => ({
                className: 'table-row u-row-link',
                onClick: () => openQuestionnaire(props.rowData),
                title: `Open ${props.rowData.name ?? 'questionnaire'}`,
              })
            },
            cell: { elementAttributes: () => ({ className: 'py-1 px-2' }) },
            paging: {
              elementAttributes: () => ({
                className: 'custom-paging'
              })
            },
            pagingPages: {
              elementAttributes: () => ({
                className: 'd-flex justify-content-end'
              })
            },
            pagingIndex: {
              elementAttributes: props => ({
                className: `pagination-item ${props.isActive ? 'active' : ''}`
              })
            },
            pagingSizes: {
              content: ({ dispatch, pageSize }) => (
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Rows per page:</span>
                  <input
                    type="number"
                    value={pageSize}
                    min={1}
                    onChange={(e) => {
                      const size = Number(e.target.value) || 1;
                      dispatch({ type: 'UpdatePageSize', pageSize: size });
                    }}
                    style={{ width: '60px', textAlign: 'center' }}
                  />
                </div>
              )
            }
          }}
        />
      ) : (
        <table className="rounded-table">
          <thead>
            <tr>
              <th className="py-1 px-2">#</th>
              <th className="py-1 px-2">Name</th>
              <th className="py-1 px-2">Account Name</th>
              <th className="py-1 px-2">Date Assigned</th>
              <th className="py-1 px-2">Due Date</th>
              <th className="py-1 px-2">Total Answered Questions</th>
            </tr>
          </thead>
          <tbody>
            <EmptyDataComponent />
          </tbody>
        </table>
      )}
    </div>
  );
}